import { FEEDBACK_VARIANTS, type FeedbackTone } from "@/components/feedback/variants";
import { Close, type IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { TRANSITION } from "@/lib/motion";
import { motion, useReducedMotion } from "motion/react";
import { forwardRef, useId, type ReactNode } from "react";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Heading, Text } from "./Typography";

type CalloutSize = "sm" | "md" | "lg";

type TextStep = "xs" | "sm" | "base";

const LAYOUT: Record<
  CalloutSize,
  { root: string; badge: string; gap: string; glyph: "xs" | "sm" | "md"; title: TextStep; body: TextStep }
> = {
  sm: { root: "gap-2 p-3", badge: "h-6 w-6", gap: "gap-0", glyph: "xs", title: "sm", body: "xs" },
  md: { root: "gap-3 p-4", badge: "h-8 w-8", gap: "gap-0.5", glyph: "sm", title: "sm", body: "sm" },
  lg: { root: "gap-3 p-4", badge: "h-8 w-8", gap: "gap-1", glyph: "sm", title: "base", body: "base" },
};

/** Live-region role of a `Callout`; `region` is a labelled landmark (named by the title), `none` stays silent. */
export type CalloutRole = "status" | "alert" | "region" | "none";

/**
 * Props of `Callout`; `role` defaults to `alert` for error/warning and `status` otherwise, `titleAs` makes the title a heading.
 * `size` md is the compact sm/sm notice, `lg` the base/base blocking notice; `emphasis="strong"` draws a full-tone border.
 */
export type CalloutProps = {
  tone: FeedbackTone;
  role?: CalloutRole;
  title?: ReactNode;
  titleAs?: "p" | "h2" | "h3";
  icon?: IconGlyph;
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  size?: CalloutSize;
  emphasis?: "soft" | "strong";
  className?: string;
  children?: ReactNode;
};

/** Inline notice tinted by tone, with optional title, action and dismiss button; errors and warnings are alerts. */
export const Callout = forwardRef<HTMLDivElement, CalloutProps>(function Callout(
  {
    tone,
    role,
    title,
    titleAs = "p",
    icon,
    action,
    onDismiss,
    dismissLabel = "Fechar aviso",
    size = "md",
    emphasis = "soft",
    className,
    children,
  },
  ref,
) {
  const { Icon: DefaultIcon, accentText, tintBg, border, borderStrong } = FEEDBACK_VARIANTS[tone];
  const reduceMotion = useReducedMotion();
  const titleId = useId();
  const layout = LAYOUT[size];
  const resolvedRole = role ?? (tone === "error" || tone === "warning" ? "alert" : "status");
  const labelledBy = resolvedRole === "region" && title ? titleId : undefined;

  return (
    <motion.div
      ref={ref}
      role={resolvedRole === "none" ? undefined : resolvedRole}
      aria-labelledby={labelledBy}
      className={cn("flex items-start rounded-card border", layout.root, tintBg, emphasis === "strong" ? borderStrong : border, className)}
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={TRANSITION.enter}
    >
      <span className={cn("grid shrink-0 place-items-center rounded-full bg-card/70", layout.badge, accentText)}>
        <Icon as={icon ?? DefaultIcon} size={layout.glyph} weight="bold" />
      </span>
      <div className={cn("flex min-w-0 flex-1 flex-col text-left", layout.gap)}>
        {title &&
          (titleAs === "p" ? (
            <Text id={titleId} size={layout.title} weight="bold" tone="default" className="break-words">
              {title}
            </Text>
          ) : (
            <Heading id={titleId} level={titleAs === "h2" ? 2 : 3} size="card" className="break-words">
              {title}
            </Heading>
          ))}
        {children && (
          <Text as="div" size={layout.body} leading="relaxed" tone="muted">
            {children}
          </Text>
        )}
        {action && <div className="mt-2">{action}</div>}
      </div>
      {onDismiss && (
        <Button
          variant="ghost"
          tone="neutral"
          size="icon-sm"
          shape="circle"
          aria-label={dismissLabel}
          onClick={onDismiss}
          className="-m-1"
        >
          <Icon as={Close} size="sm" weight="bold" />
        </Button>
      )}
    </motion.div>
  );
});
